import React, { useEffect } from "react"
import CookieConsent, { getCookieConsentValue } from "react-cookie-consent"
import { Link } from "gatsby"
import Markdown from "markdown-to-jsx"
import Cookies from "js-cookie"
import { initializeAndTrack } from "../../plugins/gatsby-plugin-gdpr-cookies-sensai"
import { isInEU } from "../utils/format"
import { getParamMetaData } from "../utils/queryParams"
import { useCheckoutState } from "../checkout/state"
import { sendQueuedEvents } from "../utils/pixel"
import { useSiteConfig } from "../hooks/useSiteConfig"
import { Box } from "@mui/system"
import usePixelEvent from "../checkout/hooks/usePixelEvent"
import { getLs, setLs } from "../utils/localStorage"
import { watchForEngaged40_40 } from "../utils/engaged40_40"

declare global {
  interface Window {
    fbq: any
    gtag: any
    _learnq: any
    trackingInit: boolean
    pixelId?: string
    gatsbyPluginGDPRCookiesOptions: any
  }
}

const COOKIE_NAME = "CookieConsent-s"

const CookiePolicy = () => {
  const state = useCheckoutState()

  const siteConfig = useSiteConfig()

  const [executePixelLp] = usePixelEvent("LpClickThrough")

  const [executePixelLead] = usePixelEvent("Lead")

  const [executeEngaged40] = usePixelEvent("Engaged_40_40")

  const initTracking = () => {
    if (window.trackingInit || !window.gatsbyPluginGDPRCookiesOptions) {
      return
    }
    window.trackingInit = true
    if (typeof location !== `undefined`) {
      initializeAndTrack(location, true, async () => {
        if (typeof window !== `undefined` && window.gtag) {
          const meta = await getParamMetaData()
          const source = meta.find(m => m.key === "utm_source")
          const campaign = meta.find(m => m.key === "utm_campaign")
          const medium = meta.find(m => m.key === "utm_medium")
          const term = meta.find(m => m.key === "utm_term")
          const content = meta.find(m => m.key === "utm_content")
          const utmId = meta.find(m => m.key === "utm_id")

          await window.gtag("set", {
            ...(source && { campaign_source: source.value }),
            ...(medium && { campaign_medium: medium.value }),
            ...(campaign && { campaign_name: campaign.value }),
            ...(term && { campaign_term: term.value }),
            ...(content && { campaign_content: content.value }),
            ...(utmId && { campaign_id: utmId.value }),
          })

          async function parseAndSendLeadEvent(data: any) {
            executePixelLead({
              email: data.$email,
              firstName: data.firstName,
              lastName: data.lastName,
              phone: data.$phone_number,
            })
          }

          window.addEventListener("klaviyoForms", function (e: any) {
            if (e.detail.type == "submit") {
              const data = e.detail.metaData
              try {
                parseAndSendLeadEvent(data)
              } catch (err) {}
            }
          })

          watchForEngaged40_40(() => {
            const storedUserData = getLs("userData") as any
            executeEngaged40({
              email: storedUserData?.email ?? undefined,
              firstName: storedUserData?.firstName ?? undefined,
              lastName: storedUserData?.lastName ?? undefined,
            })
          })

          sendQueuedEvents().then(() => {
            if (
              document &&
              document.referrer &&
              (document.referrer.includes("ubpreview.sens.ai") ||
                document.referrer.includes("get.brainyears.com") ||
                document.referrer.includes("get.sens.ai"))
            ) {
              if (!window.location.pathname.includes("/store/checkout")) {
                //send LpClickThrough event
                const storedUserData = getLs("userData") as any
                const sent = getLs("sentLPEvent") as string
                const send = !sent || new Date() > new Date(sent)
                if (send) {
                  let expiry = new Date()

                  expiry.setDate(expiry.getDate() + 1)

                  setLs("sentLPEvent", expiry.toISOString())
                  executePixelLp({
                    email: storedUserData?.email ?? undefined,
                    firstName: storedUserData?.firstName ?? undefined,
                    lastName: storedUserData?.lastName ?? undefined,
                  })
                }
              }
            }
          })
        }
      })
    }
  }

  useEffect(() => {
    if (getCookieConsentValue(COOKIE_NAME)) {
      setTimeout(() => {
        initTracking()
      }, 1200)
    }
  }, [])

  if (getCookieConsentValue(COOKIE_NAME)) {
    return <></>
  }

  if (
    state.countryInventory &&
    !isInEU(state.countryInventory) &&
    !getCookieConsentValue(COOKIE_NAME)
  ) {
    Cookies.set(COOKIE_NAME, "true", { secure: true, sameSite: "strict" })
    initTracking()

    return <></>
  }

  return siteConfig && siteConfig.cookieBanner ? (
    <CookieConsent
      location="bottom"
      buttonText="Accept"
      cookieName={COOKIE_NAME}
      style={{ alignItems: "center", fontSize: "1.25rem" }}
      buttonStyle={{ backgroundColor: "var(--color-purple)", color: "white" }}
      enableDeclineButton
      declineButtonText="Decline"
      declineButtonStyle={{
        backgroundColor: "var(--color-grey-3)",
        color: "white",
      }}
      onAccept={() => {
        initTracking()
      }}
    >
      <Box
        sx={{
          "& > p, & > p span": { fontSize: "0.85rem", lineHeight: "1.25rem" },
          "& > p": { margin: 0 },
        }}
      >
        <p>
          {siteConfig && siteConfig.cookieBanner && (
            <Markdown>{siteConfig.cookieBanner}</Markdown>
          )}{" "}
          Please refer to our privacy policy{" "}
          <Link to="/privacy-policy">here</Link>.
        </p>
      </Box>
    </CookieConsent>
  ) : null
}

export default CookiePolicy
